import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "../component/Header";
import { FiSearch, FiX, FiAlertTriangle } from "react-icons/fi";
import { API_BASE_URL } from "../utils/apiConfig";

function DeviceErrorsPage() {
    const [errorsLog, setErrorsLog] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("deviceId") || "";
    });
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;
    const [selectedErrors, setSelectedErrors] = useState(new Set());

    const fetchErrors = useCallback(async () => {
        setLoading(true);
        setError(null);
        setSelectedErrors(new Set());
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API_BASE_URL}/api/device-errors`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setErrorsLog(res.data.data || []);
            } else {
                setErrorsLog([]);
                setError("Failed to load device errors.");
            }
        } catch (err) {
            console.error(err);
            setError("Error fetching device errors.");
            setErrorsLog([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchErrors();
    }, [fetchErrors]);

    const displayedErrors = errorsLog.filter(err => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            String(err.device_id)?.toLowerCase().includes(query) ||
            err.error_code?.toLowerCase().includes(query) ||
            err.error_message?.toLowerCase().includes(query) ||
            err.user_name?.toLowerCase().includes(query) ||
            err.organization_name?.toLowerCase().includes(query)
        );
    });

    const totalPages = Math.ceil(displayedErrors.length / rowsPerPage);
    const paginatedErrors = displayedErrors.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const handleBulkDelete = async () => {
        if (selectedErrors.size === 0) return;
        const confirm = window.confirm(`Are you sure you want to delete ${selectedErrors.size} errors?`);
        if (!confirm) return;

        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/device-errors/bulk-delete`,
                { ids: Array.from(selectedErrors) },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSelectedErrors(new Set());
            fetchErrors();
        } catch (err) {
            console.error("Bulk delete failed", err);
            alert("Failed to delete errors.");
        }
    };

    const toggleSelectError = (id) => {
        const newSet = new Set(selectedErrors);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedErrors(newSet);
    };

    const toggleSelectAll = () => {
        if (selectedErrors.size === displayedErrors.length) {
            setSelectedErrors(new Set());
        } else {
            setSelectedErrors(new Set(displayedErrors.map(e => e.id)));
        }
    };


    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            <div className="grid grid-cols-1 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
                        <div>
                            <h2 className="text-xl font-semibold text-red-500 flex items-center gap-2">
                                <FiAlertTriangle /> Device Errors
                            </h2>
                            <p className="text-xs text-gray-500 mt-1">
                                Displaying {displayedErrors.length} record{displayedErrors.length !== 1 ? "s" : ""}
                            </p>
                        </div>

                        <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
                            {/* Search bar */}
                            <div className="relative flex items-center h-9">
                                <FiSearch size={13} className="absolute left-3 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search errors..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="h-full bg-black/40 border border-white/5 text-gray-300 text-[11px] font-bold rounded-xl pl-8 pr-8 focus:outline-none focus:ring-1 focus:ring-red-500 transition w-36 md:w-56 placeholder:text-gray-600"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => {
                                            setSearchQuery("");
                                            setCurrentPage(1);
                                        }}
                                        className="absolute right-3 text-gray-500 hover:text-red-400 transition"
                                    >
                                        <FiX size={13} />
                                    </button>
                                )}
                            </div>

                            {selectedErrors.size > 0 && (
                                <button
                                    onClick={handleBulkDelete}
                                    className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white text-[11px] font-black uppercase tracking-widest px-4 h-9 rounded-xl transition shrink-0"
                                >
                                    Delete ({selectedErrors.size})
                                </button>
                            )}
                            <button
                                onClick={() => fetchErrors()}
                                className="flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 text-[11px] font-black uppercase tracking-widest px-4 h-9 rounded-xl transition shrink-0"
                            >
                                ↻ Refresh
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    {loading ? (
                        <div className="text-center py-8 text-gray-400">Loading device errors...</div>
                    ) : error ? (
                        <div className="text-center py-8 text-red-400">{error}</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-gray-300">
                                <thead>
                                    <tr className="border-b border-gray-700 text-gray-400 text-left">
                                        <th className="px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={displayedErrors.length > 0 && selectedErrors.size === displayedErrors.length}
                                                onChange={toggleSelectAll}
                                                className="accent-red-500 rounded bg-black border border-gray-700 w-4 h-4"
                                                title="Select all filtered"
                                            />
                                        </th>
                                        <th className="px-4 py-3">Sr.</th>
                                        <th className="px-4 py-3">Device / Organization</th>
                                        <th className="px-4 py-3">User Info</th>
                                        <th className="px-4 py-3">Error Code</th>
                                        <th className="px-4 py-3">Message</th>
                                        <th className="px-4 py-3">Severity</th>
                                        <th className="px-4 py-3">Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedErrors.map((err, index) => (
                                        <tr key={err.id} className="border-b border-gray-800 hover:bg-[#2a1e1e] transition">
                                            <td className="px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedErrors.has(err.id)}
                                                    onChange={() => toggleSelectError(err.id)}
                                                    className="accent-red-500 rounded bg-black border border-gray-700 w-4 h-4 cursor-pointer"
                                                />
                                            </td>
                                            <td className="px-4 py-3 font-medium text-white">{(currentPage - 1) * rowsPerPage + index + 1}</td>
                                            <td className="px-4 py-3">
                                                <div className="text-white font-medium">{err.organization_name || "—"}</div>
                                                <div className="text-gray-500 text-xs">Device: {err.device_id}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-white font-medium capitalize">{err.user_name || "—"}</div>
                                                <div className="text-gray-500 text-xs">{err.user_email || "—"}</div>
                                            </td>
                                            <td className="px-4 py-3 text-red-300 font-mono text-xs">{err.error_code}</td>
                                            <td className="px-4 py-3 text-gray-300 max-w-xs truncate" title={err.error_message}>
                                                {err.error_message}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded border ${err.error_severity === 'HIGH' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                                                    err.error_severity === 'MEDIUM' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' :
                                                        'bg-gray-500/10 text-gray-400 border-gray-500/30'
                                                    }`}>
                                                    {err.error_severity || "UNKNOWN"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                                                {new Date(err.device_timestamp).toLocaleString('en-GB', {
                                                    timeZone: 'Asia/Kolkata',
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric',
                                                    hour: 'numeric',
                                                    minute: '2-digit',
                                                    hour12: true
                                                }).toUpperCase()}
                                            </td>
                                        </tr>
                                    ))}
                                    {displayedErrors.length === 0 && (
                                        <tr>
                                            <td colSpan="8" className="px-4 py-8 text-center text-gray-500 italic">No errors found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            {totalPages > 0 && (
                                <div className="flex flex-col sm:flex-row items-center justify-between mt-4 p-2 text-xs">
                                    <div className="text-gray-500 mb-4 sm:mb-0">
                                        Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, displayedErrors.length)} of {displayedErrors.length} entries
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 bg-black/40 border border-white/5 text-gray-300 rounded hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center px-4 bg-black/40 border border-white/5 rounded text-gray-300">
                                            Page {currentPage} of {totalPages}
                                        </div>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 bg-black/40 border border-white/5 text-gray-300 rounded hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DeviceErrorsPage;
